<template>
  <div class="questionnaire-form-page">

    <!-- 三列布局容器 -->
    <div class="create-survey-container-three-col">
      <!-- 第一列：问卷基础信息 -->
      <div class="basic-info-section">
        <div class="form-section">
          <h3 class="card-title-new">问卷基础信息</h3>
          <a-form layout="vertical">
            <a-form-item label="问卷标题" required>
              <a-textarea v-model="formData.title" placeholder="请输入问卷标题（最长30字）" :maxLength="30" :rows="2"
                @input="updateTitleCount" />
              <div class="char-count" :class="titleCountClass">{{ titleCount }}/30</div>
            </a-form-item>
            <a-form-item label="问卷描述">
              <a-textarea v-model="formData.description" placeholder="请输入问卷描述（最长500字）" :maxLength="500" :rows="3"
                @input="updateDescCount" />
              <div class="char-count" :class="descCountClass">{{ descCount }}/500</div>
            </a-form-item>
            <a-form-item label="感谢语">
              <a-textarea v-model="formData.thankYouText" placeholder="请输入感谢语（可选）" :maxLength="50" :rows="2"
                @input="updateThankYouTextCount" />
              <div class="char-count" :class="thankYouTextCountClass">{{ thankYouTextCount }}/50</div>
            </a-form-item>
            <a-form-item label="生效时间" required class="effective-time-form-item">
              <a-range-picker v-model="timeRange" show-time format="YYYY-MM-DD HH:mm" :placeholder="['开始时间', '结束时间']"
                @change="handleTimeRangeChange" style="width: 100%;" />
              <div v-if="errors.effectiveTime" class="error-message">{{ errors.effectiveTime }}</div>
            </a-form-item>
          </a-form>
        </div>
      </div>

      <!-- 第二列：题目列表 -->
      <div class="question-list-section">
        <div class="question-list-new">
          <h3 class="card-title-new">题目列表</h3>
          <div class="questions-wrapper">
            <template v-if="formData.questions.length > 0">
              <div class="questions-container">
                <div v-for="(question, index) in formData.questions" :key="question.questionId" class="question-item"
                  :class="{ 'is-editing': editingQuestion.questionId === question.questionId }"
                  @click="selectQuestion(question)">
                  <div class="question-header">
                    <div class="question-number">
                      <span class="question-type-badge"
                        :style="{ background: getQuestionTypeColor(question.questionType) }">{{
                          getQuestionTypeText(question.questionType) }}</span>
                      <span>Q{{ index + 1 }}</span>
                    </div>
                    <div class="question-title-text">
                      {{ question.title || '(未设置标题)' }}
                      <span v-if="question.required" class="required-mark">(必填)</span>
                    </div>
                  </div>
                  <div v-if="question.description" class="question-description">
                    {{ question.description }}
                  </div>
                  <div v-if="question.parentQuestionId" class="parent-question-info">
                    <span class="parent-question-label">上级题目：</span>
                    <span class="parent-question-text">{{ getParentQuestionDisplayText(question) }}</span>
                  </div>
                  <div class="question-content">
                    <!-- 单选/多选题预览 -->
                    <template
                      v-if="question.questionType === QuestionTypeEnum.SINGLE_CHOICE || question.questionType === QuestionTypeEnum.MULTI_CHOICE">
                      <div class="options-preview">
                        <div v-for="option in question.options" :key="option.optionId" class="option-item">
                          <input :type="question.questionType === QuestionTypeEnum.SINGLE_CHOICE ? 'radio' : 'checkbox'"
                            :name="`preview-${question.questionId}`" disabled />
                          <span>{{ option.label || '(未设置)' }}</span>
                        </div>
                      </div>
                    </template>
                    <!-- 填空题预览 -->
                    <template v-else-if="question.questionType === QuestionTypeEnum.TEXT">
                      <textarea v-if="question.inputType === InputTypeEnum.TEXTAREA"
                        placeholder="请输入" :rows="3" disabled class="h5-preview-textarea" />
                      <input v-else type="text" placeholder="请输入" disabled
                        class="h5-preview-input" />
                    </template>
                    <!-- 日期选择预览 -->
                    <template v-else-if="question.questionType === QuestionTypeEnum.DATE">
                      <div class="date-preview">
                        <a-icon type="calendar" style="margin-right: 6px; color: #999;" />
                        <span class="preview-placeholder">请选择日期</span>
                      </div>
                    </template>
                    <!-- 附件上传预览 -->
                    <template v-else-if="question.questionType === QuestionTypeEnum.FILE_UPLOAD">
                      <div class="upload-preview">
                        <a-icon type="upload" style="margin-right: 6px; color: #999;" />
                        <span class="preview-placeholder">附件上传（excel/ppt/pdf/zip/word，最大{{ (question.config && question.config.maxSize) || FileMaxSizeLimit }}MB）</span>
                      </div>
                    </template>
                    <!-- 表格问题预览 -->
                    <template v-else-if="question.questionType === QuestionTypeEnum.TABLE">
                      <div class="table-preview-wrapper">
                        <a-table
                          :columns="getTablePreviewColumns(question)"
                          :data-source="getTablePreviewDataSource(question)"
                          :pagination="false"
                          size="small"
                          bordered
                          row-key="rowIdx"
                        />
                      </div>
                    </template>
                    <!-- 级联问题预览 -->
                    <template v-else-if="question.questionType === QuestionTypeEnum.CASCADE">
                      <div class="cascade-preview">
                        <a-icon type="apartment" style="margin-right: 6px; color: #999;" />
                        <span class="preview-placeholder">级联选择</span>
                      </div>
                    </template>
                  </div>
                  <div class="question-actions">
                    <a-button size="small" @click.stop="deleteQuestion(question)" v-if="!isReadonly">删除</a-button>
                    <template v-if="!question.parentQuestionId && !isReadonly">
                      <a-button v-if="index !== 0" size="small" @click.stop="moveQuestionUp(index)">
                        ↑ 上移
                      </a-button>
                      <a-button v-if="index !== formData.questions.length - 1" size="small"
                        @click.stop="moveQuestionDown(index)">
                        ↓ 下移
                      </a-button>
                    </template>
                  </div>
                </div>
              </div>
            </template>
            <template v-else>
              <div class="questions-empty-state">
                <div class="empty-icon">📝</div>
                <p class="empty-text">暂无题目</p>
                <p class="empty-desc">点击下方按钮开始添加题目</p>
              </div>
            </template>
          </div>
          <button class="add-question-btn-new" @click="showQuestionTypeModal" :disabled="shouldDisableAddButton" v-if="!isReadonly">
            <span style="font-size: 24px;">+</span> 添加题目
          </button>
        </div>
      </div>

      <!-- 第三列：编辑面板 -->
      <div class="edit-panel-section">
        <div class="edit-panel" v-show="editingQuestion.questionId || isAddingQuestion">
          <div class="panel-header">
            <h3 class="panel-title">{{ isEditing ? '编辑题目' : '添加题目' }}</h3>
            <button class="close-panel-btn" @click="closeEditPanel">×</button>
          </div>

          <div class="panel-content">
            <a-form layout="vertical">
              <!-- 题目类型 -->
              <a-form-item label="题目类型" required>
                <a-select v-model="editingQuestion.questionType" placeholder="请选择题目类型" :disabled="isEditing"
                  @change="handleQuestionTypeChange">
                  <a-select-option v-for="option in QuestionTypeOptions" :key="option.value" :value="option.value">
                    {{ option.text }}
                  </a-select-option>
                </a-select>
              </a-form-item>

              <!-- 题目标题 -->
              <a-form-item label="题目标题" required>
                <a-input v-model="editingQuestion.title" placeholder="请输入题目标题" :maxLength="300"
                  @input="updateEditQuestionTitleCount" />
                <div class="char-count" :class="editQuestionTitleCountClass">{{ editQuestionTitleCount }}/300</div>
              </a-form-item>

              <!-- 题目说明 -->
              <a-form-item label="题目说明">
                <a-textarea v-model="editingQuestion.description" placeholder="请输入题目说明（可选）" :maxLength="300" :rows="2"
                  @input="updateEditQuestionDescCount" />
                <div class="char-count" :class="editQuestionDescCountClass">{{ editQuestionDescCount }}/300</div>
              </a-form-item>

              <!-- 是否必填 -->
              <a-form-item>
                <div class="switch-group">
                  <label class="switch">
                    <input type="checkbox" v-model="editingQuestion.required" @change="handleTableRequiredChange($event.target.checked)">
                    <span class="slider"></span>
                  </label>
                  <span class="form-label" style="margin: 0;">必填</span>
                </div>
              </a-form-item>

              <!-- 单选/多选题：选项编辑 -->
              <template
                v-if="editingQuestion.questionType === QuestionTypeEnum.SINGLE_CHOICE || editingQuestion.questionType === QuestionTypeEnum.MULTI_CHOICE">
                <div class="section-divider"></div>
                <h4 class="section-title-sm">选项设置</h4>

                <!-- 选项列表 -->
                <div class="options-edit-list">
                  <div v-for="(option, index) in editingQuestion.options" :key="option.optionId"
                    class="edit-option-item">
                    <!-- 第一行：选项编号、选项内容、可填空、删除按钮 -->
                    <div class="edit-option-row" style="display: flex; align-items: center; gap: 10px; width: 100%;">
                      <span class="edit-option-number">{{ index + 1 }}</span>
                      <a-input v-model="option.label" placeholder="请输入选项内容" :maxLength="200" style="flex: 1;" />
                      <label class="option-checkbox-label">
                        <a-checkbox v-model="option.hasInput">可填空</a-checkbox>
                      </label>
                      <a-button size="small" type="danger" icon="delete" :disabled="editingQuestion.options.length <= 2"
                        @click="removeOption(index)" />
                    </div>
                    <!-- 第二行：填空占位符输入框（当勾选可填空时显示） -->
                    <a-input v-if="option.hasInput" v-model="option.inputPlaceholder" placeholder="填空占位符(限10字)"
                      :maxLength="10" style="width: calc(100% - 34px); margin-top: 8px; margin-left: 34px;" />
                  </div>
                </div>

                <a-button type="dashed" block icon="plus" :disabled="editingQuestion.options.length >= 10"
                  @click="addOption" class="add-option-btn">
                  添加选项 ({{ editingQuestion.options.length }}/10)
                </a-button>

                <div class="section-spacing"></div>

                <!-- 多选题特殊配置 -->
                <template v-if="editingQuestion.questionType === QuestionTypeEnum.MULTI_CHOICE">
                  <div class="section-divider"></div>
                  <h4 class="section-title-sm">选择数量限制</h4>
                  <div class="config-row">
                    <span>最少选择：</span>
                    <a-input-number v-model="editingQuestion.minSelect" :min="0" :max="editingQuestion.options.length"
                      :precision="0" />
                  </div>
                  <div class="config-row">
                    <span>最多选择：</span>
                    <a-input-number v-model="editingQuestion.maxSelect" :min="0" :max="editingQuestion.options.length"
                      :precision="0" />
                  </div>
                </template>
              </template>

              <!-- 填空题配置 -->
              <template v-if="editingQuestion.questionType === QuestionTypeEnum.TEXT">
                <a-form-item label="输入类型" required>
                  <a-radio-group v-model="editingQuestion.inputType">
                    <a-radio :value="InputTypeEnum.TEXT">单行文本</a-radio>
                    <a-radio :value="InputTypeEnum.TEXTAREA">多行文本</a-radio>
                  </a-radio-group>
                </a-form-item>
                <a-form-item label="字符长度限制">
                  <div class="length-config">
                    <a-input-number v-model="editingQuestion.minLength" :min="0" :max="500" :precision="0"
                      placeholder="最小" />
                    <span>~</span>
                    <a-input-number v-model="editingQuestion.maxLength" :min="0" :max="500" :precision="0"
                      placeholder="最大" />
                  </div>
                </a-form-item>
                <a-form-item label="输入限制">
                  <a-radio-group v-model="editingQuestion.config.inputRule">
                    <a-radio v-for="option in InputRuleOptions" :key="option.value" :value="option.value">
                      {{ option.text }}
                    </a-radio>
                  </a-radio-group>
                </a-form-item>
              </template>

              <!-- 附件上传配置 -->
              <template v-if="editingQuestion.questionType === QuestionTypeEnum.FILE_UPLOAD">
                <div class="section-divider"></div>
                <h4 class="section-title-sm">上传设置</h4>
                <div class="config-row">
                  <span>文件大小限制（MB）：</span>
                  <a-input-number v-model="editingQuestion.config.maxSize" :min="1" :max="FileMaxSizeLimit" :precision="0" />
                </div>
                <div class="config-row" style="margin-top: 8px;">
                  <span>允许格式：</span>
                  <span style="color: #999;">excel、ppt、pdf、zip、word</span>
                </div>
              </template>

              <!-- 表格问题配置 -->
              <!-- 表格问题配置（列表样式） -->
              <template v-if="editingQuestion.questionType === QuestionTypeEnum.TABLE">
                <div class="section-divider"></div>
                <h4 class="section-title-sm">表格设置</h4>
                <div class="table-edit-wrapper">
                  <table class="table-edit-grid">
                    <thead>
                      <tr>
                        <th class="sticky-col">序号</th>
                        <th v-for="(col, colIdx) in editingQuestion.config.columns" :key="col.columnId">
                          <div class="col-header-cell">
                            <div class="col-header-actions">
                              <a-checkbox v-if="editingQuestion.required" v-model="col.required">必填</a-checkbox>
                              <a-button type="link" size="small"
                                :disabled="editingQuestion.config.columns.length <= 1"
                                @click="removeTableColumn(colIdx)">删除</a-button>
                            </div>
                            <a-input v-model="col.label" placeholder="列名" :maxLength="50" />
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(row, rowIdx) in editingQuestion.config.rows" :key="row.rowId">
                        <td class="sticky-col">
                          <div class="sticky-col-content">
                            <span>{{ rowIdx + 1 }}</span>
                            <a-button type="link" size="small"
                              :disabled="editingQuestion.config.rows.length <= 1"
                              @click.stop="removeTableRow(rowIdx)">删除</a-button>
                          </div>
                        </td>
                        <td v-for="col in editingQuestion.config.columns" :key="col.columnId"
                            class="data-cell"
                            :class="{ 'required-col-cell': col.required }">
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div class="table-action-row" v-if="!isReadonly">
                  <a-button type="dashed" icon="plus" @click="addTableRow" class="add-option-btn" style="flex: 1;">
                    添加行
                  </a-button>
                  <a-button type="dashed" icon="plus" @click="addTableColumn" class="add-option-btn" style="flex: 1;">
                    添加列
                  </a-button>
                </div>
              </template>

              <!-- 级联问题配置 -->
              <template v-if="editingQuestion.questionType === QuestionTypeEnum.CASCADE">
                <div class="section-divider"></div>
                <h4 class="section-title-sm">级联设置</h4>
                <a-form-item label="级联配置" required>
                  <a-tree-select
                    v-model="editingQuestion.config.cascadeAnswerType"
                    :tree-data="cascadeTreeSelectData"
                    :replace-fields="{ title: 'label', value: 'categoryValue', key: 'id' }"
                    placeholder="请选择级联配置"
                    tree-default-expand-all
                    allow-clear
                    style="width: 100%"
                    @change="handleCascadeSelectChange"
                  />
                </a-form-item>
              </template>

              <!-- 二级题目绑定 -->
              <a-form-item label="设为二级题目" style="margin-top: 10px;">
                <div class="binding-selects">
                  <div class="binding-select-group">
                    <label class="binding-select-label">一级问题</label>
                    <a-select v-model="editingQuestion.parentQuestionId" placeholder="请选择一级问题"
                      @change="handleParentQuestionDirectChange">
                      <a-select-option value="">-- 不作为二级题目 --</a-select-option>
                      <a-select-option v-for="q in availableParentQuestions" :key="q.questionId" :value="q.questionId">
                        {{ q.title }}
                      </a-select-option>
                    </a-select>
                  </div>
                  <div class="binding-select-group">
                    <label class="binding-select-label">选项</label>
                    <a-select v-model="editingQuestion.parentOptionId" placeholder="请选择选项"
                      :disabled="!editingQuestion.parentQuestionId">
                      <a-select-option value="">-- 请选择选项 --</a-select-option>
                      <a-select-option v-for="opt in parentQuestionOptions" :key="opt.optionId" :value="opt.optionId">
                        {{ opt.label }}
                      </a-select-option>
                    </a-select>
                  </div>
                </div>
              </a-form-item>
            </a-form>
          </div>

          <div class="panel-footer">
            <button class="btn btn-outline" @click="closeEditPanel">取消</button>
            <button class="btn btn-primary" @click="saveEditingQuestion" v-if="!isReadonly">保存</button>
          </div>
        </div>

        <!-- 空状态占位 -->
        <div class="edit-panel-placeholder" v-show="!editingQuestion.questionId && !isAddingQuestion">
          <div class="placeholder-content">
            <div style="font-size: 48px; color: #d1d5db;">📝</div>
            <p>点击"添加题目"开始创建问卷</p>
          </div>
        </div>
      </div>
    </div>

    <!-- 提交和取消按钮 -->
    <div class="form-actions-bar" v-if="!isReadonly">
      <button class="btn btn-outline" @click="handlePreview">预览</button>
      <div class="right-actions">
        <button class="btn btn-outline" @click="handleCancel">取消</button>
        <button class="btn btn-primary" @click="handleSave">提交</button>
      </div>
    </div>

    <!-- 题目类型选择弹窗 -->
    <div v-if="questionTypeModalVisible" class="modal-overlay" @click.self="questionTypeModalVisible = false">
      <div class="modal">
        <div class="modal-title">选择题目类型</div>
        <div class="modal-body">
          <div v-for="option in QuestionTypeOptions" :key="option.value" class="question-type-option"
            @click="addQuestion(option.value)">
            <div class="type-icon">
              <a-icon :type="getQuestionTypeIcon(option.value)" />
            </div>
            <div class="type-info">
              <div class="type-name">{{ option.text }}</div>
              <div class="type-desc">{{ getQuestionTypeDesc(option.value) }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- H5预览弹窗 -->
    <div v-if="previewModalVisible" class="modal-overlay" @click.self="previewModalVisible = false">
      <div class="modal h5-preview-modal">
        <div class="modal-title">
          问卷预览
          <button class="close-modal-btn" @click="closePreview">×</button>
        </div>
        <div class="modal-body">
          <div class="h5-preview-container">
            <div class="h5-preview-content">
              <div class="preview-header">
                <h3>{{ formData.title || '问卷标题' }}</h3>
                <p v-if="formData.description">{{ formData.description }}</p>
              </div>
              <div class="preview-questions">
                <div v-for="(question, index) in flattenedQuestions" :key="question.questionId"
                  class="preview-question-item">
                  <div class="preview-question-title">
                    <span v-if="question.required" class="required-mark">* </span>
                    <span class="question-index">{{ index + 1 }}.</span>
                    <span>{{ question.title }}</span>
                    <span v-if="question._isSubQuestion">(二级问题)</span>
                  </div>
                  <!-- 题目说明 -->
                  <div v-if="question.description" class="preview-question-description" style="font-size: 13px; color: #666; margin-bottom: 12px; line-height: 1.4;">
                    {{ question.description }}
                  </div>
                  <div class="preview-question-body">
                    <!-- 单选题 -->
                    <template v-if="question.questionType === QuestionTypeEnum.SINGLE_CHOICE">
                      <div v-for="option in question.options" :key="option.optionId" class="option-wrapper">
                        <label class="h5-preview-option">
                          <input type="radio" :name="`preview-${question.questionId}`" :value="option.optionId" disabled />
                          <span>{{ option.label }}</span>
                        </label>
                        <!-- 选项填空输入框 -->
                        <input v-if="option.hasInput" type="text"
                          :placeholder="option.inputPlaceholder || '请输入'" class="h5-preview-option-input" readonly />
                      </div>
                    </template>
                    <!-- 多选题 -->
                    <template v-else-if="question.questionType === QuestionTypeEnum.MULTI_CHOICE">
                      <div v-for="option in question.options" :key="option.optionId" class="option-wrapper">
                        <label class="h5-preview-option">
                          <input type="checkbox" :value="option.optionId" disabled />
                          <span>{{ option.label }}</span>
                        </label>
                        <!-- 选项填空输入框 -->
                        <input v-if="option.hasInput" type="text"
                          :placeholder="option.inputPlaceholder || '请输入'" class="h5-preview-option-input" readonly />
                      </div>
                    </template>
                    <!-- 填空题 -->
                    <template v-else-if="question.questionType === QuestionTypeEnum.TEXT">
                      <textarea v-if="question.inputType === InputTypeEnum.TEXTAREA"
                        placeholder="请输入"
                        :rows="3" class="h5-preview-textarea" readonly />
                      <input v-else type="text"
                        placeholder="请输入" class="h5-preview-input" readonly />
                    </template>
                    <!-- 日期选择 -->
                    <template v-else-if="question.questionType === QuestionTypeEnum.DATE">
                      <a-date-picker
                        placeholder="请选择日期"
                        format="YYYY-MM-DD"
                        disabled
                        style="width: 100%;" />
                    </template>
                    <!-- 附件上传 -->
                    <template v-else-if="question.questionType === QuestionTypeEnum.FILE_UPLOAD">
                      <div class="h5-upload-area" style="border: 1px dashed #d9d9d9; border-radius: 4px; padding: 16px; text-align: center; color: #999;">
                        <a-icon type="upload" style="font-size: 24px; color: #1890ff;" />
                        <p style="margin: 8px 0 0;">点击上传附件</p>
                        <p style="font-size: 12px; color: #bbb;">
                          支持 excel/ppt/pdf/zip/word，最大{{ (question.config && question.config.maxSize) || FileMaxSizeLimit }}MB
                        </p>
                      </div>
                    </template>
                    <!-- 表格问题 -->
                    <template v-else-if="question.questionType === QuestionTypeEnum.TABLE">
                      <div class="h5-table-preview" style="overflow-x: auto;">
                        <table style="width: 100%; border-collapse: collapse; font-size: 13px;">
                          <thead>
                            <tr>
                              <th style="border: 1px solid #e8e8e8; padding: 6px 8px; background: #fafafa; min-width: 60px;">序号</th>
                              <th v-for="col in (question.config && question.config.columns || [])" :key="col.columnId"
                                style="border: 1px solid #e8e8e8; padding: 6px 8px; background: #fafafa; min-width: 80px;">
                                {{ col.label || '(未命名)' }}
                                <span v-if="col.required" style="color: #e74c3c; margin-left: 4px;">*</span>
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="(row, rowIdx) in (question.config && question.config.rows || [])" :key="row.rowId">
                              <td style="border: 1px solid #e8e8e8; padding: 6px 8px; background: #fafafa; font-weight: 500;">
                                {{ rowIdx + 1 }}
                              </td>
                              <td v-for="col in (question.config && question.config.columns || [])" :key="col.columnId"
                                style="border: 1px solid #e8e8e8; padding: 4px 6px;">
                                <input type="text" placeholder="请输入" readonly
                                  style="width: 100%; border: none; outline: none; font-size: 13px;" />
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </template>
                    <!-- 级联问题 -->
                    <template v-else-if="question.questionType === QuestionTypeEnum.CASCADE">
                      <a-cascader
                        :placeholder="question.description || '请选择'"
                        disabled
                        style="width: 100%;" />
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import {
  QuestionTypeEnum,
  QuestionTypeTextEnum,
  QuestionTypeOptions,
  InputTypeEnum,
  InputTypeTextEnum,
  InputRuleEnum,
  InputRuleTextEnum,
  InputRuleOptions,
  CascadeQuestionTypeEnum,
  CascadeAnswerTypeEnum,
  FileAcceptTypes,
  FileAcceptExtensionMap,
  FileMaxSizeLimit,
} from './help';
import {
  createQuestionnaire,
  updateQuestionnaire,
  getQuestionnaireDetail,
  getCascadeDictTree,
} from '@/api/questionnaireManage';

export default {
  name: 'QuestionnaireForm',
  props: {
    isEdit: {
      type: Boolean,
      default: false,
    },
    questionnaireId: {
      type: String,
      default: '',
    },
    isReadonly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      QuestionTypeEnum,
      QuestionTypeTextEnum,
      QuestionTypeOptions,
      InputTypeEnum,
      InputTypeTextEnum,
      InputRuleEnum,
      InputRuleTextEnum,
      InputRuleOptions,
      CascadeQuestionTypeEnum,
      CascadeAnswerTypeEnum,
      FileAcceptTypes,
      FileAcceptExtensionMap,
      FileMaxSizeLimit,

      thankYouTextCount: 0,
      timeRange: null,

      formData: {
        title: '',
        description: '',
        thankYouText: '',
        startTime: '',
        endTime: '',
        questions: [],
      },

      errors: {
        title: '',
        description: '',
        effectiveTime: '',
      },

      // 字数统计
      titleCount: 0,
      descCount: 0,
      editQuestionTitleCount: 0,
      editQuestionDescCount: 0,

      // 编辑状态
      isAddingQuestion: false,
      isEditing: false,
      editingQuestion: {},
      originalQuestionId: '',
      originalEditingQuestion: null,  // 保存原始编辑题目，用于变更检测

      // 弹窗
      questionTypeModalVisible: false,
      previewModalVisible: false,

      // 级联问题树形数据（从后端接口获取）
      cascadeDictTree: [],
    };
  },
  computed: {
    availableParentQuestions() {
      return this.formData.questions.filter(q => {
        // 排除自身
        if (q.questionId === this.editingQuestion.questionId) return false;
        // 只允许单选/多选题作为父题目
        if (q.questionType !== QuestionTypeEnum.SINGLE_CHOICE &&
            q.questionType !== QuestionTypeEnum.MULTI_CHOICE) return false;
        // 排除已作为二级题目的题目（有 parentQuestionId 的）
        if (q.parentQuestionId) return false;
        return true;
      });
    },
    parentQuestionOptions() {
      if (!this.editingQuestion || !this.editingQuestion.parentQuestionId) return [];
      const parentQuestion = this.formData.questions.find(
        q => q.questionId === this.editingQuestion.parentQuestionId
      );
      return parentQuestion ? parentQuestion.options : [];
    },
    // 字数统计class
    titleCountClass() {
      return this.getCountClass(this.titleCount, 30);
    },
    descCountClass() {
      return this.getCountClass(this.descCount, 500);
    },
    editQuestionTitleCountClass() {
      return this.getCountClass(this.editQuestionTitleCount, 30);
    },
    editQuestionDescCountClass() {
      return this.getCountClass(this.editQuestionDescCount, 50);
    },
    thankYouTextCountClass() {
      return this.getCountClass(this.thankYouTextCount, 50);
    },
    // 获取平铺的问题列表（二级问题紧跟在对应的一级问题后面）
    flattenedQuestions() {
      const result = [];
      const topLevel = this.formData.questions.filter(q => !q.parentQuestionId);

      topLevel.forEach(parentQ => {
        // 先添加一级问题
        result.push(parentQ);

        // 找出该一级问题的所有二级问题
        const subQuestions = this.formData.questions.filter(q =>
          q.parentQuestionId === parentQ.questionId
        );

        // 添加二级问题
        subQuestions.forEach(subQ => {
          result.push({
            ...subQ,
            _isSubQuestion: true,              // 标记为二级问题
            _parentQuestion: parentQ,          // 保存父问题引用
            _parentOption: parentQ.options?.find(opt => opt.optionId === subQ.parentOptionId)  // 保存关联的选项
          });
        });
      });

      return result;
    },
    // 级联树形下拉框数据（顶层不可选，只能选子节点）
    cascadeTreeSelectData() {
      return (this.cascadeDictTree || []).map(item => ({
        ...item,
        selectable: false,
      }));
    },

    // ==================== 状态管理 ====================
    // 判断当前是否有未保存的修改
    hasUnsavedChanges() {
      // 查看模式下不检查未保存状态
      if (this.isReadonly) {
        return false;
      }
      if (!this.editingQuestion || !this.editingQuestion.questionId) {
        return this.isAddingQuestion; // 添加状态也算有未保存内容
      }
      return this.hasQuestionChanged(this.editingQuestion, this.originalEditingQuestion);
    },

    // 判断是否应该禁用添加按钮
    shouldDisableAddButton() {
      // 查看模式下不启用添加按钮的禁用逻辑
      if (this.isReadonly) {
        return false;
      }
      return (this.isAddingQuestion || this.isEditing) && this.hasUnsavedChanges;
    },
  },
  mounted() {
    if (this.questionnaireId) {
      this.loadQuestionnaireData();
    }
    this.loadCascadeDictTree();
  },
  methods: {
    // ==================== 状态管理方法 ====================
    // 深度对比两个题目对象是否相同
    hasQuestionChanged(current, original) {
      if (!current || !original) return false;
      return JSON.stringify(current) !== JSON.stringify(original);
    },

    // 显示未保存修改警告对话框
    showUnsavedChangesWarning(callback) {
      // 查看模式下不显示未保存提示，直接执行回调
      if (this.isReadonly) {
        if (callback) callback();
        return;
      }
      this.$confirm({
        title: '提示',
        content: '当前题目未保存，如果继续操作将不会保存当前修改，确定要继续吗？',
        okText: '确定',
        cancelText: '取消',
        onOk: () => {
          this.clearUnsavedChanges();
          if (callback) callback();
        }
      });
    },

    // 清除未保存的修改状态
    clearUnsavedChanges() {
      this.isAddingQuestion = false;
      this.isEditing = false;
      this.editingQuestion = {};
      this.originalEditingQuestion = null;
    },

    // ==================== 字数统计 ====================
    getCountClass(count, max) {
      const ratio = count / max;
      if (ratio >= 1) return 'error';
      if (ratio >= 0.9) return 'warning';
      return '';
    },
    updateTitleCount() {
      this.titleCount = (this.formData.title || '').length;
    },
    updateDescCount() {
      this.descCount = (this.formData.description || '').length;
    },
    updateThankYouTextCount() {
      this.thankYouTextCount = (this.formData.thankYouText || '').length;
    },
    updateEditQuestionTitleCount() {
      this.editQuestionTitleCount = (this.editingQuestion.title || '').length;
    },
    updateEditQuestionDescCount() {
      this.editQuestionDescCount = (this.editingQuestion.description || '').length;
    },

    // ==================== 时间范围 ====================
    handleTimeRangeChange(dates) {
      if (dates && dates.length === 2) {
        this.timeRange = dates;
        this.formData.startTime = dates[0].format('YYYY-MM-DD HH:mm');
        this.formData.endTime = dates[1].format('YYYY-MM-DD HH:mm');
        this.$set(this.errors, 'effectiveTime', '');
      } else {
        this.timeRange = null;
        this.formData.startTime = '';
        this.formData.endTime = '';
      }
    },

    // ==================== 题目操作 ====================
    showQuestionTypeModal() {
      this.questionTypeModalVisible = true;
    },
    addQuestion(type) {
      // 如果有未保存修改，先提示
      if (this.hasUnsavedChanges) {
        this.showUnsavedChangesWarning(() => {
          this.createNewQuestion(type);
        });
        return;
      }
      this.createNewQuestion(type);
    },

    // 新增辅助方法
    createNewQuestion(type) {
      this.isAddingQuestion = true;
      this.isEditing = false;
      this.editingQuestion = this.getEmptyQuestion();
      this.editingQuestion.questionType = type;
      this.editingQuestion.sortOrder = this.formData.questions.length + 1;
      this.originalEditingQuestion = null;

      if (type === QuestionTypeEnum.SINGLE_CHOICE || type === QuestionTypeEnum.MULTI_CHOICE) {
        this.editingQuestion.options = [
          { optionId: `opt_${Date.now()}_1`, label: '' },
          { optionId: `opt_${Date.now()}_2`, label: '' }
        ];
      }

      // 新题型初始化 config
      if (type === QuestionTypeEnum.TEXT) {
        this.editingQuestion.config = {
          inputRule: InputRuleEnum.NONE,
        };
      } else if (type === QuestionTypeEnum.FILE_UPLOAD) {
        this.editingQuestion.config = {
          acceptTypes: [...FileAcceptTypes],
          maxSize: FileMaxSizeLimit,
          maxCount: 1,
        };
      } else if (type === QuestionTypeEnum.TABLE) {
        const ts = Date.now();
        this.editingQuestion.config = {
          columns: [
            { columnId: `col_${ts}_1`, label: '', required: true },
            { columnId: `col_${ts}_2`, label: '', required: true },
          ],
          rows: [
            { rowId: `row_${ts}_1`, label: '' },
          ],
        };
      } else if (type === QuestionTypeEnum.CASCADE) {
        this.editingQuestion.config = {
          cascadeQuestionType: undefined,
          cascadeAnswerType: undefined,
        };
      }

      this.editQuestionTitleCount = 0;
      this.editQuestionDescCount = 0;
      this.questionTypeModalVisible = false;
    },
    selectQuestion(question) {
      // 点击题目卡片选中并编辑
      this.editQuestion(question);
    },
    editQuestion(question) {
      // 如果有未保存修改，先提示
      if (this.hasUnsavedChanges) {
        this.showUnsavedChangesWarning(() => {
          this.loadQuestionForEdit(question);
        });
        return;
      }
      this.loadQuestionForEdit(question);
    },

    // 新增辅助方法
    loadQuestionForEdit(question) {
      this.isAddingQuestion = false;
      this.isEditing = true;
      this.editingQuestion = JSON.parse(JSON.stringify(question));
      this.originalEditingQuestion = JSON.parse(JSON.stringify(question));
      this.originalQuestionId = question.questionId;
      this.editQuestionTitleCount = (question.title || '').length;
      this.editQuestionDescCount = (question.description || '').length;
    },
    deleteQuestion(question) {
      this.$confirm({
        title: '确认删除',
        content: `确定要删除题目"${question.title}"吗？`,
        onOk: () => {
          const index = this.formData.questions.findIndex(q => q.questionId === question.questionId);
          if (index > -1) {
            this.formData.questions.splice(index, 1);
            this.formData.questions.forEach((q, i) => {
              q.sortOrder = i + 1;
            });
            // 如果正在编辑此题目，关闭编辑面板
            if (this.editingQuestion.questionId === question.questionId) {
              this.closeEditPanel();
            }
          }
        },
      });
    },
    closeEditPanel() {
      if (this.hasUnsavedChanges) {
        this.showUnsavedChangesWarning(() => {
          this.clearUnsavedChanges();
        });
        return;
      }
      this.clearUnsavedChanges();
    },
    getEmptyQuestion() {
      return {
        questionId: '',
        questionType: QuestionTypeEnum.SINGLE_CHOICE,
        title: '',
        description: '',
        required: true,
        sortOrder: 1,
        parentQuestionId: '',
        parentOptionId: '',
        options: [],
        minSelect: 1,
        maxSelect: undefined,
        inputType: InputTypeEnum.TEXT,
        minLength: 0,
        maxLength: 500,
        // 新题型 config
        config: {},
      };
    },

    // ==================== 题目编辑 ====================
    handleQuestionTypeChange() {
      const type = this.editingQuestion.questionType;
      if (type === QuestionTypeEnum.TEXT) {
        this.editingQuestion.options = [];
        this.editingQuestion.config = {
          inputRule: InputRuleEnum.NONE,
        };
      } else if (type === QuestionTypeEnum.SINGLE_CHOICE || type === QuestionTypeEnum.MULTI_CHOICE) {
        if (!this.editingQuestion.options || this.editingQuestion.options.length === 0) {
          this.editingQuestion.options = [
            { optionId: `opt_${Date.now()}_1`, label: '', hasInput: false, inputPlaceholder: '' },
            { optionId: `opt_${Date.now()}_2`, label: '', hasInput: false, inputPlaceholder: '' }
          ];
        }
        this.editingQuestion.config = {};
      } else if (type === QuestionTypeEnum.DATE) {
        this.editingQuestion.options = [];
        this.editingQuestion.config = {};
      } else if (type === QuestionTypeEnum.FILE_UPLOAD) {
        this.editingQuestion.options = [];
        this.editingQuestion.config = {
          acceptTypes: [...FileAcceptTypes],
          maxSize: FileMaxSizeLimit,
          maxCount: 1,
        };
      } else if (type === QuestionTypeEnum.TABLE) {
        this.editingQuestion.options = [];
        const ts = Date.now();
        this.editingQuestion.config = {
          columns: [
            { columnId: `col_${ts}_1`, label: '', required: true },
            { columnId: `col_${ts}_2`, label: '', required: true },
          ],
          rows: [
            { rowId: `row_${ts}_1`, label: '' },
          ],
        };
      } else if (type === QuestionTypeEnum.CASCADE) {
        this.editingQuestion.options = [];
        this.editingQuestion.config = {
          cascadeQuestionType: undefined,
          cascadeAnswerType: undefined,
        };
      }
    },
    addOption() {
      if (this.editingQuestion.options.length >= 10) {
        this.$message.warning('最多只能添加10个选项');
        return;
      }
      this.editingQuestion.options.push({
        optionId: `opt_${Date.now()}`,
        label: '',
        hasInput: false,
        inputPlaceholder: '',
      });
    },
    removeOption(index) {
      if (this.editingQuestion.options.length <= 2) {
        this.$message.warning('至少需要2个选项');
        return;
      }
      this.editingQuestion.options.splice(index, 1);
    },

    // ==================== 表格问题操作 ====================
    addTableColumn() {
      if (!this.editingQuestion.config.columns) {
        this.$set(this.editingQuestion.config, 'columns', []);
      }
      this.editingQuestion.config.columns.push({
        columnId: `col_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
        label: '',
        required: this.editingQuestion.required === true,  // 根据表格必填状态决定
      });
    },
    removeTableColumn(index) {
      if (this.editingQuestion.config.columns.length <= 1) {
        this.$message.warning('至少需要1列');
        return;
      }
      this.editingQuestion.config.columns.splice(index, 1);
    },
    addTableRow() {
      if (!this.editingQuestion.config.rows) {
        this.$set(this.editingQuestion.config, 'rows', []);
      }
      this.editingQuestion.config.rows.push({
        rowId: `row_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
        label: '',
      });
    },
    removeTableRow(index) {
      if (this.editingQuestion.config.rows.length <= 1) {
        this.$message.warning('至少需要1行');
        return;
      }
      this.editingQuestion.config.rows.splice(index, 1);
    },
    // 处理表格必填切换
    handleTableRequiredChange(newVal) {
      if (newVal === true && this.editingQuestion.questionType === QuestionTypeEnum.TABLE) {
        const columns = this.editingQuestion.config?.columns;
        if (columns && columns.length > 0) {
          const hasRequiredColumn = columns.some(col => col.required === true);
          if (!hasRequiredColumn) {
            columns[0].required = true;
            this.$message.info('已自动将第一列设为必填');
          }
        }
      }
    },

    // ==================== 级联问题操作 ====================
    async loadCascadeDictTree() {
      try {
        const res = await getCascadeDictTree();
        if (res.code == 200 && res.data) {
          this.cascadeDictTree = res.data;
        }
      } catch (error) {
        console.error('加载级联字典失败:', error);
      }
    },
    handleCascadeSelectChange(value) {
      if (!value) {
        this.editingQuestion.config.cascadeQuestionType = undefined;
        this.editingQuestion.config.cascadeAnswerType = undefined;
        return;
      }
      // 从树数据中找到父节点，自动设置 cascadeQuestionType
      for (const parent of this.cascadeDictTree) {
        if (parent.children && parent.children.some(child => child.categoryValue === value)) {
          this.editingQuestion.config.cascadeQuestionType = parent.categoryValue;
          return;
        }
      }
    },

    // ==================== 题目保存校验 ====================
    validateEditingQuestion(question) {
      const q = question || this.editingQuestion;

      if (!q.title || q.title.trim() === '') {
        this.$message.error('请输入题目标题');
        return false;
      }
      if (q.title.length > 30) {
        this.$message.error('题目标题不能超过30字');
        return false;
      }

      if (q.parentQuestionId && !q.parentOptionId) {
        this.$message.error('请选择父题对应的选项');
        return false;
      }
      if (q.parentOptionId && !q.parentQuestionId) {
        this.$message.error('请选择选项对应的父题');
        return false;
      }

      if (q.questionType === QuestionTypeEnum.SINGLE_CHOICE) {
        if (q.options.length < 2) {
          this.$message.error('单选题至少需要2个选项');
          return false;
        }
        for (let i = 0; i < q.options.length; i++) {
          if (!q.options[i].label || q.options[i].label.trim() === '') {
            this.$message.error(`请完善第${i + 1}个选项的内容`);
            return false;
          }
          // 新增：选项字数校验
          if (q.options[i].label && q.options[i].label.length > 200) {
            this.$message.error(`第${i + 1}个选项内容不能超过200字`);
            return false;
          }
        }
      }

      if (q.questionType === QuestionTypeEnum.MULTI_CHOICE) {
        if (q.options.length < 2) {
          this.$message.error('多选题至少需要2个选项');
          return false;
        }
        for (let i = 0; i < q.options.length; i++) {
          if (!q.options[i].label || q.options[i].label.trim() === '') {
            this.$message.error(`请完善第${i + 1}个选项的内容`);
            return false;
          }
          // 新增：选项字数校验
          if (q.options[i].label && q.options[i].label.length > 200) {
            this.$message.error(`第${i + 1}个选项内容不能超过200字`);
            return false;
          }
        }
        if (q.minSelect !== undefined && q.maxSelect !== undefined && q.minSelect > q.maxSelect) {
          this.$message.error('最少选择数不能大于最多选择数');
          return false;
        }
        if (q.minSelect !== undefined && q.minSelect > q.options.length) {
          this.$message.error('最少选择数不能超过选项总数');
          return false;
        }
        if (q.maxSelect !== undefined && q.maxSelect > q.options.length) {
          this.$message.error('最多选择数不能超过选项总数');
          return false;
        }
      }

      if (q.questionType === QuestionTypeEnum.TEXT) {
        if (!q.inputType) {
          this.$message.error('请选择输入类型');
          return false;
        }
        if (q.minLength !== undefined && q.maxLength !== undefined && q.minLength > q.maxLength) {
          this.$message.error('最小字符长度不能大于最大字符长度');
          return false;
        }
        // 新增：字符长度范围校验
        if (q.minLength !== undefined && q.minLength < 0) {
          this.$message.error('最小字符长度不能为负数');
          return false;
        }
        if (q.maxLength !== undefined && q.maxLength > 10000) {
          this.$message.error('最大字符长度不能超过10000');
          return false;
        }
      }

      // 附件上传校验
      if (q.questionType === QuestionTypeEnum.FILE_UPLOAD) {
        if (q.config && q.config.maxSize !== undefined) {
          if (q.config.maxSize < 1 || q.config.maxSize > FileMaxSizeLimit) {
            this.$message.error(`文件大小限制范围为 1~${FileMaxSizeLimit}MB`);
            return false;
          }
        }
      }

      // 表格问题校验
      if (q.questionType === QuestionTypeEnum.TABLE) {
        if (!q.config || !q.config.columns || q.config.columns.length < 1) {
          this.$message.error('表格至少需要1列');
          return false;
        }
        if (!q.config || !q.config.rows || q.config.rows.length < 1) {
          this.$message.error('表格至少需要1行');
          return false;
        }
        // 校验列名不能为空
        if (q.config.columns) {
          for (let i = 0; i < q.config.columns.length; i++) {
            if (!q.config.columns[i].label || q.config.columns[i].label.trim() === '') {
              this.$message.error(`第${i + 1}列名称不能为空`);
              return false;
            }
          }
        }
        // 新增：如果表格是必填的，至少需要有一列是必填的
        if (q.required && q.config && q.config.columns) {
          const hasRequiredColumn = q.config.columns.some(col => col.required === true);
          if (!hasRequiredColumn) {
            this.$message.error('表格问题为必填时，至少需要有一列是必填的');
            return false;
          }
        }
      }

      // 级联问题校验
      if (q.questionType === QuestionTypeEnum.CASCADE) {
        if (!q.config || !q.config.cascadeAnswerType) {
          this.$message.error('请选择级联配置');
          return false;
        }
      }

      return true;
    },
    saveEditingQuestion() {
      if (!this.validateEditingQuestion()) {
        return;
      }

      if (!this.editingQuestion.questionId) {
        this.editingQuestion.questionId = `q_${Date.now()}`;
      }

      if (this.editingQuestion.questionType === QuestionTypeEnum.MULTI_CHOICE) {
        if (this.editingQuestion.minSelect === undefined) {
          this.editingQuestion.minSelect = 1;
        }
        if (this.editingQuestion.maxSelect === undefined) {
          this.editingQuestion.maxSelect = this.editingQuestion.options.length;
        }
      }

      if (this.isEditing && this.originalQuestionId) {
        const index = this.formData.questions.findIndex(q => q.questionId === this.originalQuestionId);
        if (index > -1) {
          this.$set(this.formData.questions, index, JSON.parse(JSON.stringify(this.editingQuestion)));
        }
      } else {
        this.formData.questions.push(JSON.parse(JSON.stringify(this.editingQuestion)));
      }

      this.clearUnsavedChanges();
      this.$message.success('题目保存成功');
    },

    // ==================== 数据转换方法 ====================
    // 将后端数据转换为前端格式
    processBackendQuestions(questions) {
      return questions.map(q => {
        const base = {
          ...q,  // 保留所有后端字段
          questionId: q.id || q.questionId || `q_${Date.now()}_${Math.random()}`,
          required: !!q.required,
          parentQuestionId: q.parentQuestionId || '',
          parentOptionId: q.parentOptionId || '',
          options: (q.options || []).map(opt => ({
            ...opt,
            optionId: opt.id || opt.optionId || `opt_${Date.now()}_${Math.random()}`,
            hasInput: !!opt.hasInput,
          })),
          config: {},
        };

        // 多选题配置解析
        if (q.questionType === QuestionTypeEnum.MULTI_CHOICE && q.config) {
          try {
            const config = JSON.parse(q.config);
            base.minSelect = config.minSelect || 1;
            base.maxSelect = config.maxSelect || (q.options || []).length;
          } catch (e) {
            console.warn('解析 config 失败:', q.config, e);
            base.minSelect = 1;
            base.maxSelect = (q.options || []).length;
          }
        } else if (q.questionType === QuestionTypeEnum.MULTI_CHOICE) {
          base.minSelect = 1;
          base.maxSelect = (q.options || []).length;
        }

        // 填空题配置解析
        if (q.questionType === QuestionTypeEnum.TEXT && q.config) {
          try {
            const config = JSON.parse(q.config);
            base.inputType = config.inputType || InputTypeEnum.TEXT;
            base.minLength = config.minLength || 0;
            base.maxLength = config.maxLength || 200;
            base.config = {
              inputRule: config.inputRule || InputRuleEnum.NONE,
            };
          } catch (e) {
            console.warn('解析填空题 config 失败:', q.config, e);
            base.inputType = InputTypeEnum.TEXT;
            base.minLength = 0;
            base.maxLength = 200;
            base.config = {
              inputRule: InputRuleEnum.NONE,
            };
          }
        } else if (q.questionType === QuestionTypeEnum.TEXT) {
          base.inputType = InputTypeEnum.TEXT;
          base.minLength = 0;
          base.maxLength = 200;
          base.config = {
            inputRule: InputRuleEnum.NONE,
          };
        }

        // 附件上传配置解析
        if (q.questionType === QuestionTypeEnum.FILE_UPLOAD && q.config) {
          try {
            base.config = JSON.parse(q.config);
          } catch (e) {
            console.warn('解析附件上传 config 失败:', q.config, e);
            base.config = { acceptTypes: [...FileAcceptTypes], maxSize: FileMaxSizeLimit, maxCount: 1 };
          }
        }

        // 表格问题配置解析
        if (q.questionType === QuestionTypeEnum.TABLE && q.config) {
          try {
            base.config = JSON.parse(q.config);
            // 兼容处理：为没有 required 字段的列设置默认值
            if (base.config.columns && Array.isArray(base.config.columns)) {
              base.config.columns.forEach(col => {
                if (col.required === undefined) {
                  col.required = false;
                }
              });
            }
          } catch (e) {
            console.warn('解析表格问题 config 失败:', q.config, e);
            base.config = { columns: [], rows: [] };
          }
        }

        // 级联问题配置解析
        if (q.questionType === QuestionTypeEnum.CASCADE && q.config) {
          try {
            base.config = JSON.parse(q.config);
          } catch (e) {
            console.warn('解析级联问题 config 失败:', q.config, e);
            base.config = { cascadeQuestionType: undefined, cascadeAnswerType: undefined };
          }
        }

        return base;
      });
    },

    // 将前端数据转换为后端格式
    formatQuestionsForSave(questions) {
      return questions.map((q, index) => {
        const question = {
          id: q.questionId,
          questionType: q.questionType,
          title: q.title,
          description: q.description,
          required: q.required ? 1 : 0,
          sortOrder: index + 1,
          options: (q.options || []).map((opt, optIndex) => ({
            id: opt.optionId,
            label: opt.label || '',
            hasInput: opt.hasInput ? 1 : 0,
            inputPlaceholder: opt.inputPlaceholder || '',
            sortOrder: optIndex + 1,
          })),
          config: null,
          parentQuestionId: q.parentQuestionId || '',
          parentOptionId: q.parentOptionId || '',
        };
        if (q.questionType === QuestionTypeEnum.MULTI_CHOICE) {
          question.config = JSON.stringify({
            minSelect: q.minSelect || 1,
            maxSelect: q.maxSelect || (q.options || []).length,
          });
        }

        // 填空题配置
        if (q.questionType === QuestionTypeEnum.TEXT) {
          question.config = JSON.stringify({
            inputType: q.inputType || InputTypeEnum.TEXT,
            minLength: q.minLength || 0,
            maxLength: q.maxLength || 200,
            inputRule: (q.config && q.config.inputRule) || InputRuleEnum.NONE,
          });
        }

        // 附件上传配置
        if (q.questionType === QuestionTypeEnum.FILE_UPLOAD) {
          question.config = JSON.stringify({
            acceptTypes: (q.config && q.config.acceptTypes) || [...FileAcceptTypes],
            maxSize: (q.config && q.config.maxSize) || FileMaxSizeLimit,
            maxCount: 1,
          });
        }

        // 表格问题配置
        if (q.questionType === QuestionTypeEnum.TABLE) {
          question.config = JSON.stringify({
            columns: (q.config && q.config.columns) || [],
            rows: (q.config && q.config.rows) || [],
          });
        }

        // 级联问题配置
        if (q.questionType === QuestionTypeEnum.CASCADE) {
          question.config = JSON.stringify({
            cascadeQuestionType: (q.config && q.config.cascadeQuestionType) || undefined,
            cascadeAnswerType: (q.config && q.config.cascadeAnswerType) || undefined,
          });
        }

        return question;
      });
    },

    // ==================== 二级题目绑定 ====================
    handleParentQuestionDirectChange() {
      // 清空选项选择
      this.editingQuestion.parentOptionId = '';
    },
    getParentQuestionDisplayText(question) {
      if (!question.parentQuestionId) return '';

      const parentQuestion = this.formData.questions.find(q => q.questionId === question.parentQuestionId);
      if (!parentQuestion) return '';

      const parentOption = parentQuestion.options?.find(opt => opt.optionId === question.parentOptionId);
      const optionText = parentOption ? parentOption.label : '未知选项';

      return `${parentQuestion.title || '未命名题目'} - ${optionText}`;
    },

    // ==================== 上移下移 ====================
    moveQuestionUp(index) {
      if (index <= 0) return;

      const questions = [...this.formData.questions];
      [questions[index - 1], questions[index]] = [questions[index], questions[index - 1]];
      this.formData.questions = questions;

      // 更新排序
      this.formData.questions.forEach((q, i) => {
        q.sortOrder = i + 1;
      });

      this.$message.success('上移成功');
    },
    moveQuestionDown(index) {
      if (index >= this.formData.questions.length - 1) return;

      const questions = [...this.formData.questions];
      [questions[index], questions[index + 1]] = [questions[index + 1], questions[index]];
      this.formData.questions = questions;

      // 更新排序
      this.formData.questions.forEach((q, i) => {
        q.sortOrder = i + 1;
      });

      this.$message.success('下移成功');
    },

    // ==================== 预览 ====================
    handlePreview() {
      if (!this.formData.title) {
        this.$message.warning('请先输入问卷标题');
        return;
      }
      if (this.formData.questions.length === 0) {
        this.$message.warning('请先添加题目');
        return;
      }

      this.previewModalVisible = true;
    },

    // 关闭预览弹窗
    closePreview() {
      this.previewModalVisible = false;
    },

    // 获取二级问题
    getSubQuestions(parentQuestionId, parentOptionId) {
      return this.formData.questions.filter(q =>
        q.parentQuestionId === parentQuestionId &&
        q.parentOptionId === parentOptionId
      );
    },


    // ==================== 工具方法 ====================
    getQuestionTypeText(type) {
      return QuestionTypeTextEnum[type] || '';
    },
    getQuestionTypeIcon(type) {
      const iconMap = {
        [QuestionTypeEnum.SINGLE_CHOICE]: 'check-circle',
        [QuestionTypeEnum.MULTI_CHOICE]: 'check-square',
        [QuestionTypeEnum.TEXT]: 'file-text',
        [QuestionTypeEnum.DATE]: 'calendar',
        [QuestionTypeEnum.FILE_UPLOAD]: 'upload',
        [QuestionTypeEnum.TABLE]: 'table',
        [QuestionTypeEnum.CASCADE]: 'apartment',
      };
      return iconMap[type] || 'file-text';
    },
    getQuestionTypeDesc(type) {
      const descMap = {
        [QuestionTypeEnum.SINGLE_CHOICE]: '用于二选一或多选一的场景',
        [QuestionTypeEnum.MULTI_CHOICE]: '用于可多选的场景',
        [QuestionTypeEnum.TEXT]: '用于收集文本信息',
        [QuestionTypeEnum.DATE]: '用于收集日期信息',
        [QuestionTypeEnum.FILE_UPLOAD]: '用于收集文件附件',
        [QuestionTypeEnum.TABLE]: '用于收集表格形式的数据',
        [QuestionTypeEnum.CASCADE]: '用于级联选择数据（如地区）',
      };
      return descMap[type] || '';
    },
    getQuestionTypeColor(type) {
      const colorMap = {
        [QuestionTypeEnum.SINGLE_CHOICE]: '#3498db',
        [QuestionTypeEnum.MULTI_CHOICE]: '#9b59b6',
        [QuestionTypeEnum.TEXT]: '#27ae60',
        [QuestionTypeEnum.DATE]: '#e67e22',
        [QuestionTypeEnum.FILE_UPLOAD]: '#1abc9c',
        [QuestionTypeEnum.TABLE]: '#e74c3c',
        [QuestionTypeEnum.CASCADE]: '#f39c12',
      };
      return colorMap[type] || '#666';
    },
    getInputTypeText(type) {
      return InputTypeTextEnum[type] || '';
    },

    // ==================== 表格预览 ====================
    getTablePreviewColumns(question) {
      const cols = (question.config && question.config.columns) || [];
      const h = this.$createElement;
      return [
        { title: '序号', dataIndex: 'rowIdx', key: 'rowIdx', width: 60, fixed: 'left', align: 'center' },
        ...cols.map(col => ({
          title: () => h('div', {
            style: { display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }
          }, [
            col.required ? h('span', {
              style: { color: '#e74c3c', marginRight: '4px' }
            }, '*') : null,
            h('span', col.label || '(未命名)')
          ]),
          dataIndex: col.columnId,
          key: col.columnId,
          // 移除固定宽度，改为自适应
        })),
      ];
    },
    getTablePreviewDataSource(question) {
      const rows = (question.config && question.config.rows) || [];
      return rows.map((row, idx) => ({
        rowIdx: idx + 1,
        ...row,
      }));
    },
    getTablePreviewScrollX(question) {
      const cols = (question.config && question.config.columns) || [];
      // 计算实际需要的宽度：序号列60px + 每列120px
      return 60 + cols.length * 120;
    },
    getTablePreviewWidth(question) {
      const cols = (question.config && question.config.columns) || [];
      // 计算表格实际宽度：序号列60px + 每列120px
      const actualWidth = 60 + cols.length * 120;
      // 如果超过容器最大宽度(约540px)，返回undefined让表格100%宽度
      return actualWidth > 540 ? undefined : actualWidth;
    },

    // ==================== 保存/取消 ====================
    async handleSave() {
      if (!this.formData.title || this.formData.title.trim() === '') {
        this.$message.error('请输入问卷标题');
        return;
      }
      if (this.formData.title.length > 30) {
        this.$message.error('问卷标题不能超过30字');
        return;
      }

      if (!this.formData.startTime || !this.formData.endTime) {
        this.$message.error('请选择生效时间');
        return;
      }
      if (new Date(this.formData.endTime) <= new Date(this.formData.startTime)) {
        this.$message.error('结束时间必须大于开始时间');
        return;
      }

      if (this.formData.questions.length === 0) {
        this.$message.error('请至少添加一道题目');
        return;
      }

      for (const question of this.formData.questions) {
        if (!this.validateEditingQuestion(question)) {
          return;
        }
      }

      const saveData = {
        ...this.formData,
        startTime: this.formData.startTime ? this.formData.startTime + ':00' : '',
        endTime: this.formData.endTime ? this.formData.endTime + ':00' : '',
        questions: this.formatQuestionsForSave(this.formData.questions),
      };

      try {
        let res;
        if (this.isEdit && this.questionnaireId) {
          res = await updateQuestionnaire({
            ...saveData,
            id: this.questionnaireId,
          });
        } else {
          res = await createQuestionnaire(saveData);
        }

        if (res.code == 200) {
          this.$message.success(this.isEdit ? '问卷更新成功' : '问卷创建成功');
          this.$router.back();
        } else {
          this.$message.error(res.message || '保存失败');
        }
      } catch (error) {
        this.$message.error('保存失败，请稍后重试');
        console.error(error);
      }
    },
    handleCancel() {
      this.$confirm({
        title: '确认取消',
        content: '取消后将丢失未保存的内容，确定要取消吗？',
        onOk: () => {
          this.$router.back();
        },
      });
    },

    // ==================== API集成 ====================
    async loadQuestionnaireData() {
      try {
        const res = await getQuestionnaireDetail(this.questionnaireId);
        if (res.code == 200 && res.data) {
          const data = res.data;
          this.formData = {
            title: data.title || '',
            description: data.description || '',
            thankYouText: data.thankYouText || '',
            startTime: data.startTime || '',
            endTime: data.endTime || '',
            questions: this.processBackendQuestions(data.questions || []),
          };
          this.timeRange = data.startTime && data.endTime
            ? [moment(data.startTime), moment(data.endTime)]
            : null;
          this.updateTitleCount();
          this.updateDescCount();
          this.updateThankYouTextCount();
        } else {
          this.$message.error(res.message || '加载问卷数据失败');
        }
      } catch (error) {
        this.$message.error('加载问卷数据失败');
        console.error(error);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
// ==================== 全局样式 ====================
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  background: #f5f5f5;
}

// 全局覆盖 antd 输入框样式，匹配原型 .form-input
::v-deep .ant-input,
::v-deep .ant-input-number-input {
  padding: 10px 15px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #3498db;
    box-shadow: none;
  }
}

::v-deep .ant-input-number {
  width: 100%;
  border-radius: 6px;

  &:focus {
    outline: none;
    border-color: #3498db;
    box-shadow: none;

    .ant-input-number-input {
      border: none;
    }
  }
}

::v-deep .ant-select-selector {
  border: 1px solid #ddd !important;
  border-radius: 6px !important;

  .ant-select-selection-search-input {
    padding: 10px 15px;
  }
}

::v-deep .ant-select-focused .ant-select-selector {
  border-color: #3498db !important;
  box-shadow: none !important;
}


// ==================== 导航栏 ====================
.nav-bar {
  padding: 15px 30px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 100;

  .nav-actions {
    display: flex;
    gap: 10px;
  }
}

// ==================== 按钮样式 ====================
.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;

  &.btn-primary {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;

    &:hover {
      opacity: 0.9;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
    }
  }

  &.btn-success {
    background: #27ae60;
    color: white;
  }

  &.btn-success:hover {
    background: #229954;
  }

  &.btn-danger {
    background: #e74c3c;
    color: white;
  }

  &.btn-danger:hover {
    background: #c0392b;
  }

  &.btn-outline {
    background: white;
    border: 1px solid #ddd;
    color: #666;
  }

  &.btn-outline:hover {
    border-color: #3498db;
    color: #3498db;
  }

  &.btn-sm {
    padding: 5px 12px;
    font-size: 12px;
  }
}

// ==================== 页面容器 ====================
.questionnaire-form-page {}

// ==================== 表单操作栏 ====================
.form-actions-bar {
  max-width: 1500px;
  margin: 20px auto 0;
  padding: 0 40px 0 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.right-actions {
  display: flex;
  gap: 12px;
}

// ==================== 三列布局容器 ====================
.create-survey-container-three-col {
  display: grid;
  grid-template-columns: 400px 600px 600px;
  gap: 20px;
  max-width: 1700px;
  margin: 0 auto;
  padding: 20px 20px 0;
  align-items: start;
}

// ==================== 第一列：问卷基础信息 ====================
.basic-info-section {
  .form-section {
    background: white;
    border-radius: 8px;
    padding: 20px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
    border: 1px solid #f0f0f0;
    height: 800px;
  }

  .card-title-new {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 16px;
    color: #1f2937;
  }

  .char-count {
    text-align: right;
    font-size: 12px;
    color: #6b7280;
    margin-top: 4px;

    &.warning {
      color: #f59e0b;
    }

    &.error {
      color: #ef4444;
    }
  }

  .error-message {
    color: #e74c3c;
    font-size: 12px;
    margin-top: 4px;
  }

  .effective-time-form-item {
    ::v-deep .ant-picker-input {
      padding: 8px 11px !important;
      height: auto !important;
    }

    ::v-deep .ant-input {
      padding: 8px 11px !important;
      height: auto !important;
    }
  }
}

// ==================== 第二列：题目列表 ====================
.question-list-section {
  .question-list-new {
    background: white;
    border-radius: 8px;
    padding: 20px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
    border: 1px solid #f0f0f0;
    height: 800px;
    display: flex;
    flex-direction: column;
  }

  .card-title-new {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 16px;
    color: #1f2937;
    flex-shrink: 0;
  }

  .questions-wrapper {
    flex: 1;
    overflow-y: auto;
    padding-right: 8px;

    // 隐藏滚动条
    &::-webkit-scrollbar {
      display: none;
    }

    scrollbar-width: none;
    /* Firefox */

    -ms-overflow-style: none;
    /* IE and Edge */
  }

  .questions-container {
    min-height: 100px;
  }

  .questions-empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    min-height: 300px;
    color: #9ca3af;
  }

  .empty-icon {
    font-size: 64px;
    margin-bottom: 16px;
    opacity: 0.5;
  }

  .empty-text {
    font-size: 16px;
    font-weight: 500;
    color: #6b7280;
    margin: 0 0 8px 0;
  }

  .empty-desc {
    font-size: 14px;
    color: #9ca3af;
    margin: 0;
  }

  .question-item {
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 20px;
    margin-bottom: 16px;
    transition: all 0.2s;
    cursor: pointer;

    &:hover {
      border-color: #667eea;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }

    &.is-editing {
      border-color: #667eea;
      background: #f0f4ff;
    }
  }

  .question-header {
    display: flex;
    align-items: center;
    margin-bottom: 10px;
    gap: 10px;
  }

  .question-number {
    display: flex;
    align-items: center;
    gap: 10px;
    font-weight: 600;
    color: #2c3e50;
    flex-shrink: 0;
  }

  .question-type-badge {
    padding: 2px 8px;
    color: white;
    border-radius: 4px;
    font-size: 11px;
  }

  .question-title-text {
    font-weight: 500;
    flex: 1;
    white-space: normal;
    word-wrap: break-word;
    word-break: break-all;
    line-height: 1.4;
  }

  .question-description {
    margin-top: 4px;
    font-size: 13px;
    color: #666;
    line-height: 1.4;
    word-wrap: break-word;
    word-break: break-all;
  }

  .question-content {
    margin: 10px 0;
    padding: 10px;
    background: white;
    border-radius: 6px;
  }

  .options-preview {
    .option-item {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px;

      input[type="radio"],
      input[type="checkbox"] {
        width: 18px;
        height: 18px;
      }
    }
  }

  .text-input-simulation {

    input,
    textarea {
      width: 100%;
      padding: 8px 12px;
      background: #f5f5f5;
      border: 1px solid #ddd;
      border-radius: 4px;
      color: #999;
      font-size: 14px;
    }

    textarea {
      min-height: 60px;
      resize: vertical;
    }
  }

  .required-mark {
    color: #e74c3c;
    font-size: 12px;
    font-weight: normal;
    margin-left: 6px;
  }

  .parent-question-info {
    display: flex;
    flex-direction: row; /* 横向排列 */
    align-items: center;
    gap: 6px;
    margin-bottom: 10px;
    padding-left: 60px; /* 与题目编号对齐，增加10px */
    font-size: 12px;
    color: #6b7280;
  }

  .parent-question-label {
    color: #9ca3af;
    flex-shrink: 0;
  }

  .parent-question-text {
    color: #667eea;
    font-weight: 500;
    word-wrap: break-word; /* 文字过长时换行 */
    word-break: break-all;
    max-width: 100%; /* 不限制宽度 */
  }

  .question-actions {
    display: flex;
    gap: 8px;
    margin-top: 10px;

    // 覆盖 antd 按钮样式，匹配原型 .btn-outline .btn-sm
    ::v-deep .ant-btn {
      padding: 5px 12px;
      font-size: 12px;
      border: 1px solid #ddd;
      background: white;
      color: #666;
      border-radius: 6px;
      height: auto;

      &:hover {
        border-color: #3498db;
        color: #3498db;
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }
  }

  .add-question-btn-new {
    width: 100%;
    padding: 16px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border: none;
    border-radius: 12px;
    font-size: 16px;
    font-weight: 500;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: all 0.2s;
    flex-shrink: 0;
    margin-top: 16px;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none;
      box-shadow: none;
    }

    span {
      font-size: 24px;
    }
  }
}

// ==================== 第三列：编辑面板 ====================
.edit-panel-section {
  position: sticky;
  top: 20px;
}

// ==================== 侧边编辑面板 ====================
.edit-panel {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  height: 800px;
  display: flex;
  flex-direction: column;

  .panel-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 16px;
    border-bottom: 1px solid #e5e7eb;
    flex-shrink: 0;
  }

  .panel-title {
    font-size: 18px;
    font-weight: 600;
    color: #1f2937;
  }

  .close-panel-btn {
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    color: #6b7280;
    padding: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;

    &:hover {
      background: #f3f4f6;
    }
  }

  .panel-content {
    flex: 1;
    overflow-y: auto;
    margin-top: 20px;
    padding-right: 8px;

    // 隐藏滚动条
    &::-webkit-scrollbar {
      display: none;
    }

    scrollbar-width: none;
    /* Firefox */

    -ms-overflow-style: none;
    /* IE and Edge */

    .section-divider {
      height: 1px;
      background: #e5e7eb;
      margin: 20px 0;
    }

    .section-title-sm {
      font-size: 14px;
      font-weight: 600;
      color: #374151;
      margin-bottom: 12px;
    }
  }

  .panel-footer {
    display: flex;
    gap: 10px;
    padding-top: 20px;
    border-top: 1px solid #e5e7eb;
    flex-shrink: 0;
  }
}

.edit-panel-placeholder {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  height: 800px;
  display: flex;
  flex-direction: column;

  .placeholder-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 1;
    color: #9ca3af;
  }
}

// ==================== 选项编辑 ====================
.options-edit-list {
  .edit-option-item {
    margin-bottom: 12px;

    // 覆盖 antd 输入框样式，匹配 prototype .edit-option-input
    ::v-deep .ant-input {
      padding: 10px 14px;
      border: 1px solid #d1d5db;
      border-radius: 8px;
      font-size: 14px;

      &:focus {
        outline: none;
        border-color: #667eea;
        box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
      }
    }

    // 选项复选框标签样式
    .option-checkbox-label {
      display: flex;
      align-items: center;
      white-space: nowrap;
      font-size: 13px;
      color: #555;
      cursor: pointer;

      ::v-deep .ant-checkbox-wrapper {
        font-size: 13px;
      }
    }

    // 覆盖删除按钮样式，匹配 prototype .edit-delete-option-btn
    ::v-deep .ant-btn.ant-btn-danger {
      background: none;
      border: none;
      color: #ef4444;
      padding: 8px;
      font-size: 18px;
      height: auto;
      line-height: 1;
      display: inline-flex;
      align-items: center;
      justify-content: center;

      &:hover {
        background: #fee2e2;
        border-color: transparent;
      }
    }
  }

  .edit-option-number {
    color: #6b7280;
    font-size: 14px;
    width: 24px;
    flex-shrink: 0;
  }
}

.config-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;

  >span {
    white-space: nowrap;
    min-width: fit-content;
  }

  ::v-deep .ant-input-number {
    flex: 1;
    min-width: 120px;
  }
}

.section-spacing {
  margin-top: 20px;
}

.length-config {
  display: flex;
  align-items: center;
  gap: 10px;
}

// 覆盖添加选项按钮样式
.add-option-btn {
  border: 1px dashed #d1d5db !important;
  border-radius: 8px !important;
  background: none !important;
  color: #6b7280 !important;

  &:hover {
    border-color: #667eea !important;
    color: #667eea !important;
    background: #f0f4ff !important;
  }
}

// 二级题目绑定样式
.binding-selects {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 8px;
}

.binding-select-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.binding-select-label {
  font-size: 13px;
  font-weight: 500;
  color: #555;
  white-space: nowrap;
}

// ==================== 开关控件 ====================
.switch-group {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.switch {
  position: relative;
  width: 44px;
  height: 24px;
  display: inline-block;

  input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  .slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #d1d5db;
    transition: 0.3s;
    border-radius: 24px;

    &:before {
      position: absolute;
      content: "";
      height: 18px;
      width: 18px;
      left: 3px;
      bottom: 3px;
      background-color: white;
      transition: 0.3s;
      border-radius: 50%;
    }
  }

  input:checked+.slider {
    background-color: #667eea;
  }

  input:checked+.slider:before {
    transform: translateX(20px);
  }
}

// ==================== 题目类型选择弹窗 ====================
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);

  // 隐藏滚动条
  &::-webkit-scrollbar {
    display: none;
  }

  scrollbar-width: none;
  /* Firefox */

  -ms-overflow-style: none;
  /* IE and Edge */

  .modal-title {
    padding: 20px;
    font-size: 18px;
    font-weight: 600;
    border-bottom: 1px solid #e5e7eb;
    position: relative;
  }

  .close-modal-btn {
    position: absolute;
    right: 20px;
    top: 20px;
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    color: #6b7280;
  }

  .modal-body {
    padding: 20px;
  }

  .modal-footer {
    padding: 15px 20px;
    border-top: 1px solid #e5e7eb;
    display: flex;
    gap: 10px;
    justify-content: flex-end;
  }
}

.question-type-option {
  display: flex;
  align-items: center;
  padding: 15px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  margin-bottom: 12px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #667eea;
    background: #f0f4ff;
  }

  .type-icon {
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 8px;
    margin-right: 15px;
    font-size: 24px;
    color: white;
  }

  .type-info {
    .type-name {
      font-size: 16px;
      font-weight: 600;
      color: #1f2937;
      margin-bottom: 4px;
    }

    .type-desc {
      font-size: 13px;
      color: #6b7280;
    }
  }
}

// ==================== H5预览 ====================
.h5-preview-modal {
  max-width: 420px;

  .h5-preview-container {
    .h5-preview-content {
      max-height: 500px;
      overflow-y: auto;
      background: #fff;

      // 隐藏滚动条
      &::-webkit-scrollbar {
        display: none;
      }

      scrollbar-width: none;
      /* Firefox */

      -ms-overflow-style: none;
      /* IE and Edge */

      .preview-header {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
        padding: 30px 20px;
        text-align: center;

        h3 {
          margin: 0;
          font-size: 22px;
          font-weight: 600;
        }

        p {
          font-size: 14px;
          opacity: 0.9;
          margin-top: 8px;
          color: white;
        }
      }

      .preview-questions {
        padding: 20px;

        .preview-question-item {
          margin-bottom: 20px;
          padding: 15px;
          background: #f8f9fa;
          border-radius: 8px;

          .preview-question-title {
            font-size: 16px;
            font-weight: 500;
            color: #2c3e50;
            margin-bottom: 15px;

            .question-index {
              margin-right: 4px;
            }

            .required-mark {
              color: #e74c3c;
              margin-left: 4px;
            }
          }

          .preview-question-body {
            padding-left: 0;
          }
        }
      }
    }
  }
}

// 选项包装器
.option-wrapper {
  margin-bottom: 16px; /* 添加底部间距，确保不被遮挡 */
}

// H5预览选项样式
.h5-preview-option {
  display: flex;
  align-items: center;
  padding: 12px 15px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  margin-bottom: 10px;
  cursor: pointer;

  input[type="radio"],
  input[type="checkbox"] {
    width: 20px;
    height: 20px;
    margin-right: 10px;
    cursor: pointer;
  }

  span {
    flex: 1;
    font-size: 14px;
    color: #333;
  }
}

// H5预览输入框样式
.h5-preview-textarea,
.h5-preview-input {
  width: 100%;
  padding: 12px 15px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  color: #333;
  resize: none;

  &:focus {
    outline: none;
    border-color: #667eea;
  }
}

.h5-preview-textarea {
  min-height: 80px;
  font-family: inherit;
}

// H5预览选项填空输入框样式
.h5-preview-option-input {
  width: calc(100% - 30px);
  margin-left: 30px;
  margin-bottom: 8px;
  padding: 8px 12px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-size: 13px;

  &:focus {
    outline: none;
    border-color: #667eea;
  }
}

// 二级问题容器
.sub-questions-container {
  margin-top: 10px;
  margin-bottom: 10px; /* 添加底部间距 */
  width: 100%;
}

// 二级问题样式
.sub-question {
  margin-left: 12px;
  padding: 10px;
  background: #fff9e6;
  border-left: 3px solid #667eea;
  border-radius: 0 8px 8px 0;
  margin-top: 8px;

  .sub-question-title {
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 8px;
    color: #555;

    &::before {
      content: '└─ ';
      color: #667eea;
      font-weight: 600;
    }

    .required-mark {
      color: #e74c3c;
      margin-left: 4px;
    }
  }

  // 二级问题内的选项样式
  .h5-preview-option {
    padding: 8px 10px;
    margin-bottom: 6px;
    font-size: 13px;
  }
}

// ==================== 新题型预览样式 ====================
.date-preview,
.upload-preview,
.cascade-preview {
  display: flex;
  align-items: center;
  padding: 8px 0;

  .preview-placeholder {
    color: #999;
    font-size: 13px;
  }
}

.table-preview-wrapper {
  margin-top: 8px;
  max-width: 100%;
  // overflow-x: auto;

  ::v-deep .ant-table {
    font-size: 12px;
  }
}

// 编辑面板 — 表格列表样式
.table-edit-wrapper {
  overflow-x: auto;
}

.table-edit-grid {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13px;
  min-width: 300px;

  th,
  td {
    border: 1px solid #e8e8e8;
    padding: 6px 8px;
    white-space: nowrap;
  }

  th {
    background: #fafafa;
    font-weight: 500;
  }

  .sticky-col {
    position: sticky;
    left: 0;
    z-index: 1;
    background: #fff;
    width: 100px;
    min-width: 100px;
    text-align: center;
    // 右侧阴影分界线，替代被 border-collapse 影响的边框
    box-shadow: 2px 0 4px rgba(0, 0, 0, 0.06);
  }

  thead .sticky-col {
    background: #fafafa;
    z-index: 2;
  }

  .sticky-col-content {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;

    span {
      flex-shrink: 0;
    }

    ::v-deep .ant-btn-link {
      font-size: 12px;
      padding: 0;
      height: auto;
      line-height: 1;
    }
  }

  .col-header-cell {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 140px;

    // 第一行：必填和删除按钮
    .col-header-actions {
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;

      ::v-deep .ant-checkbox-wrapper {
        font-size: 12px;
      }

      ::v-deep .ant-btn-link {
        font-size: 12px;
        padding: 0;
        height: auto;
        line-height: 1;
      }
    }
  }

  .data-cell {
    min-width: 140px;  // 改为只设置最小宽度，允许自适应
  }

  // 必填列的单元格样式
  .required-col-cell {
    background: #fff9f9;
  }
}

.table-action-row {
  display: flex;
  gap: 12px;
  margin-top: 12px;
}

.config-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
}
</style>
